import z from "zod";

import {
  zJsonDomainError,
  zJsonErrorWrapper,
  zJsonFormValidationError,
  zJsonHttpReadError,
  zJsonTechnicalError,
  zJsonUnexpectedError,
} from "@/.generated/hey/v4b-backend/zod.gen";
import { EV4bClientErrorCategory } from "@/data/api/kili/common/kili-errors.type";
import { V4bApiError } from "@/data/api/v4b/error/v4b-client-error.types";

const zV4bApiError = z.discriminatedUnion("errorCategory", [
  zJsonErrorWrapper.extend({
    errorCategory: z.literal(EV4bClientErrorCategory.DOMAIN_ERROR),
    errorDetails: zJsonDomainError.nullish(),
  }),
  zJsonErrorWrapper.extend({
    errorCategory: z.literal(EV4bClientErrorCategory.FORM_VALIDATION_ERROR),
    errorDetails: zJsonFormValidationError.nullish(),
  }),
  zJsonErrorWrapper.extend({
    errorCategory: z.literal(EV4bClientErrorCategory.HTTP_READ_ERROR),
    errorDetails: zJsonHttpReadError.nullish(),
  }),
  zJsonErrorWrapper.extend({
    errorCategory: z.literal(EV4bClientErrorCategory.TECHNICAL_ERROR),
    errorDetails: zJsonTechnicalError.nullish(),
  }),
  zJsonErrorWrapper.extend({
    errorCategory: z.literal(EV4bClientErrorCategory.UNEXPECTED_ERROR),
    errorDetails: zJsonUnexpectedError.nullish(),
  }),
]);

export function mapV4bApiError(error: unknown): V4bApiError | undefined {
  const result = zV4bApiError.safeParse(error);
  if (!result.success) {
    return undefined;
  }

  const { errorCategory, errorDetails } = result.data;
  switch (errorCategory) {
    case EV4bClientErrorCategory.DOMAIN_ERROR:
    case EV4bClientErrorCategory.TECHNICAL_ERROR:
      return {
        errorCategory,
        errorDetails: errorDetails
          ? {
              errorType: errorDetails.errorType,
              errorCode: errorDetails.errorCode,
              message: errorDetails.message ?? undefined,
            }
          : undefined,
      };
    case EV4bClientErrorCategory.FORM_VALIDATION_ERROR:
      return {
        errorCategory,
        errorDetails: errorDetails
          ? {
              fieldErrors: errorDetails.fieldErrors?.map((fieldError) => ({
                errorCode: fieldError.errorCode ?? undefined,
                message: fieldError.message,
                propertyPath: fieldError.propertyPath ?? undefined,
              })),
            }
          : undefined,
      };
    case EV4bClientErrorCategory.HTTP_READ_ERROR:
      return {
        errorCategory,
        errorDetails: errorDetails ? { message: errorDetails.message } : undefined,
      };
    case EV4bClientErrorCategory.UNEXPECTED_ERROR:
      return {
        errorCategory,
        errorDetails: errorDetails
          ? {
              errorType: errorDetails.errorType,
              message: errorDetails.message ?? undefined,
            }
          : undefined,
      };
  }
}
